// List of reliable news sources for article validation
export const RELIABLE_NEWS_SOURCES = [
  'bbc.com',
  'theguardian.com',
  'reuters.com',
  'aljazeera.com',
  'aje.io',
  'cnn.com',
  'nytimes.com',
  'washingtonpost.com',
  'apnews.com',
  'npr.org',
  'ft.com',
  'abc.net.au',
  'telegraph.co.uk'
];

export interface ArticleMetadata {
  title: string;
  description: string;
  thumbnail: string;
  author?: string;
  source: string;
}

export const validateArticleUrl = (url: string): { isValid: boolean; source?: string; error?: string } => {
  try {
    const urlObj = new URL(url);
    const domain = urlObj.hostname.toLowerCase();
    
    // Check if the domain is in our list of reliable sources
    const isReliableSource = RELIABLE_NEWS_SOURCES.some(source => 
      domain === source || domain.endsWith(`.${source}`)
    );
    
    if (!isReliableSource) {
      return {
        isValid: false,
        error: 'Please provide a URL from a reliable news source. We accept articles from BBC, Reuters, CNN, New York Times, and other major news outlets.'
      };
    }
    
    return {
      isValid: true,
      source: domain
    };
  } catch (error) {
    return {
      isValid: false,
      error: 'Please enter a valid URL.'
    };
  }
};

export const extractArticleMetadata = async (url: string): Promise<ArticleMetadata> => {
  const urlObj = new URL(url);
  const source = urlObj.hostname.toLowerCase();
  
  // List of CORS proxies to try in order
  const corsProxies = [
    `https://api.allorigins.win/get?url=${encodeURIComponent(url)}`,
    `https://cors-anywhere.herokuapp.com/${url}`,
    `https://thingproxy.freeboard.io/fetch/${url}`,
    `https://corsproxy.io/?${encodeURIComponent(url)}`
  ];
  
  for (const proxyUrl of corsProxies) {
    try {
      console.log(`Trying CORS proxy: ${proxyUrl}`);
      const response = await fetch(proxyUrl, {
        method: 'GET',
        headers: {
          'Accept': 'text/html,application/xhtml+xml,application/xml;q=0.9,image/webp,*/*;q=0.8',
          'Accept-Language': 'en-US,en;q=0.5',
          'User-Agent': 'Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/91.0.4472.124 Safari/537.36'
        },
        signal: AbortSignal.timeout(10000) // 10 second timeout
      });
      
      if (!response.ok) {
        console.log(`Proxy failed with status: ${response.status}`);
        continue; // Try next proxy
      }
      
      let html: string;
      if (proxyUrl.includes('allorigins.win')) {
        const data = await response.json();
        html = data.contents;
      } else {
        html = await response.text();
      }
      
      if (!html || html.length < 100) {
        console.log('Proxy returned insufficient HTML content');
        continue; // Try next proxy
      }
      
      // Create a DOM parser to extract meta tags
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      
      // Extract title - try multiple methods
      const title = 
        doc.querySelector('meta[property="og:title"]')?.getAttribute('content') ||
        doc.querySelector('meta[name="twitter:title"]')?.getAttribute('content') ||
        doc.querySelector('meta[property="article:title"]')?.getAttribute('content') ||
        doc.querySelector('title')?.textContent ||
        'Untitled Article';
      
      // Extract description - try multiple methods
      const description = 
        doc.querySelector('meta[property="og:description"]')?.getAttribute('content') ||
        doc.querySelector('meta[name="twitter:description"]')?.getAttribute('content') ||
        doc.querySelector('meta[name="description"]')?.getAttribute('content') ||
        doc.querySelector('meta[property="article:description"]')?.getAttribute('content') ||
        'No description available';
      
      // Extract thumbnail - try multiple methods
      let thumbnail = 
        doc.querySelector('meta[property="og:image"]')?.getAttribute('content') ||
        doc.querySelector('meta[name="twitter:image"]')?.getAttribute('content') ||
        doc.querySelector('meta[name="image"]')?.getAttribute('content') ||
        doc.querySelector('meta[property="twitter:image:src"]')?.getAttribute('content') ||
        doc.querySelector('meta[property="og:image:secure_url"]')?.getAttribute('content');
      
      // If no meta tags found, try to find the first image in the article
      if (!thumbnail || thumbnail === '/assets/emojis/1f4c5.svg') {
        const firstImage = doc.querySelector('img[src*="http"]');
        if (firstImage) {
          const imgSrc = firstImage.getAttribute('src');
          if (imgSrc && imgSrc.startsWith('http')) {
            thumbnail = imgSrc;
          }
        }
      }
      
      // If still no thumbnail, try to find any image with a reasonable size
      if (!thumbnail || thumbnail === '/assets/emojis/1f4c5.svg') {
        const images = Array.from(doc.querySelectorAll('img[src*="http"]'));
        for (const img of images) {
          const imgSrc = img.getAttribute('src');
          const width = img.getAttribute('width');
          const height = img.getAttribute('height');
          
          // Prefer images that are likely to be thumbnails (reasonable size)
          if (imgSrc && imgSrc.startsWith('http')) {
            const imgWidth = parseInt(width || '0');
            const imgHeight = parseInt(height || '0');
            
            // If image has reasonable dimensions (not too small, not too large)
            if ((imgWidth >= 200 && imgWidth <= 1200) || (imgHeight >= 200 && imgHeight <= 1200)) {
              thumbnail = imgSrc;
              break;
            }
            
            // If no dimensions specified, use the first image as fallback
            if (!width && !height) {
              thumbnail = imgSrc;
              break;
            }
          }
        }
      }
      
      // Final fallback
      if (!thumbnail || thumbnail === '/assets/emojis/1f4c5.svg') {
        thumbnail = '/assets/emojis/1f4c5.svg';
      }
      
      // Extract author
      let author = 
        doc.querySelector('meta[name="author"]')?.getAttribute('content') ||
        doc.querySelector('meta[property="article:author"]')?.getAttribute('content') ||
        undefined;
      
      // Clean up author field - remove URLs and keep only actual author names
      if (author) {
        // Remove URLs from author field
        author = author.replace(/https?:\/\/[^\s]+/g, '').trim();
        // Remove common URL patterns
        author = author.replace(/www\.[^\s]+/g, '').trim();
        // Remove empty or invalid author names
        if (!author || author.length < 2 || author.includes('http')) {
          author = undefined;
        }
      }
      
      console.log('Successfully extracted metadata from:', proxyUrl);
      return {
        title: title.trim(),
        description: description.trim(),
        thumbnail,
        author,
        source
      };
      
    } catch (error) {
      console.log(`Proxy failed: ${proxyUrl}`, error);
      continue; // Try next proxy
    }
  }
  
  // If all proxies fail, return fallback metadata
  console.log('All CORS proxies failed, using fallback metadata');
  return {
    title: 'Article from ' + source,
    description: 'No description available - metadata extraction failed',
    thumbnail: '/assets/emojis/1f4c5.svg',
    source
  };
}; 